import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import "./ProjectPage.scss";

import { bigProjects, jamProjects, websiteProjects, portfolioShowcase, theme as siteTheme } from "../../portfolio";
import { getProjectStatusMeta, stripStatusTags } from "../../helpers/projectStatus";

export default function ProjectPage() {
  const { id } = useParams();
  const history = useHistory();

  // Scroll to top whenever the viewed project changes (initial open, or Prev/Next)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Merge projects into one list (supports the unified, filterable portfolio section)
  const allProjects = useMemo(
    () =>
      portfolioShowcase?.items?.length > 0
        ? portfolioShowcase.items
        : [...bigProjects.projects, ...jamProjects.projects, ...websiteProjects.projects],
    // Source data is static module-level config; recompute only if that reference changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const project = allProjects.find((p) => p.id === id);

  // Per-project browser tab title, so multiple project pages open in different
  // tabs are distinguishable. Restores whatever the title was before this page
  // (the site default) once you actually leave the project page.
  const originalTitleRef = useRef(document.title);

  useEffect(() => {
    if (project?.projectName) {
      document.title = `${project.projectName} | Georgios Grigoriadis`;
    }
  }, [project]);

  useEffect(() => {
    const originalTitle = originalTitleRef.current;
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // --------------------- HELPERS ----------------------
  function getPrimaryUrl(p) {
    return (
      p?.primaryUrl ||
      (p?.footerLink && p.footerLink[0] && p.footerLink[0].url) ||
      null
    );
  }

  function getYouTubeId(input) {
    if (!input) return null;
    const s = String(input).trim();

    // If already looks like a video id
    if (!s.includes("/") && !s.includes(".") && s.length >= 8) return s;

    // Common URL formats
    // - https://www.youtube.com/watch?v=VIDEOID
    // - https://youtu.be/VIDEOID
    // - https://www.youtube.com/embed/VIDEOID
    try {
      const url = new URL(s);
      if (url.hostname.includes("youtu.be")) {
        const id = url.pathname.replace("/", "").trim();
        return id || null;
      }
      const v = url.searchParams.get("v");
      if (v) return v;
      const parts = url.pathname.split("/").filter(Boolean);
      const embedIndex = parts.indexOf("embed");
      if (embedIndex >= 0 && parts[embedIndex + 1]) return parts[embedIndex + 1];
      return null;
    } catch {
      return null;
    }
  }

  // --------------------- HOOKS MUST RUN BEFORE ANY RETURN ----------------------
  const screenshots = useMemo(() => project?.screenshots || [], [project]);

  const initialHero =
    project?.heroImage ||
    project?.gif ||
    (screenshots.length > 0 ? screenshots[0] : null);

  const [activeImage, setActiveImage] = useState(initialHero);

  // Media support (YouTube + MP4)
  const youTubeInput =
    project?.trailerYouTube ||
    project?.youtube ||
    project?.youtubeUrl ||
    project?.videoYouTube ||
    project?.videoUrl; // backwards compatible

  const youTubeId = getYouTubeId(youTubeInput);
  const mp4Src =
    project?.trailerMp4 ||
    project?.mp4 ||
    project?.videoMp4 ||
    project?.videoFile;

  const mediaChoices = useMemo(() => {
    const list = [];
    if (mp4Src) {
      list.push({
        key: "mp4",
        label: "MP4",
        type: "mp4",
        src: mp4Src,
        poster: project?.trailerPoster || project?.poster || null,
      });
    }
    if (youTubeId) {
      list.push({
        key: "youtube",
        label: "YouTube",
        type: "youtube",
        id: youTubeId,
      });
    }
    return list;
  }, [mp4Src, youTubeId, project]);

  const [activeMedia, setActiveMedia] = useState(
    mediaChoices.length > 0 ? mediaChoices[0].key : null
  );

  // Derived values (safe even when project is missing).
  // Keep these hooks ABOVE any early return to satisfy react-hooks/rules-of-hooks.
  const details = useMemo(() => {
    if (!project) return [];
    const rows = [];
    if (project.year) rows.push({ label: "Year", value: project.year });
    if (project.platform) rows.push({ label: "Platform", value: project.platform });
    if (project.role) rows.push({ label: "Role", value: project.role });
    if (project.teamSize) rows.push({ label: "Team", value: project.teamSize });
    if (project.duration) rows.push({ label: "Duration", value: project.duration });
    if (project.engine) rows.push({ label: "Engine", value: project.engine });
    return rows;
  }, [project]);

  const activeMediaObj = useMemo(() => {
    if (!activeMedia) return null;
    return mediaChoices.find((m) => m.key === activeMedia) || null;
  }, [mediaChoices, activeMedia]);

  // Unified filmstrip: trailer(s) + screenshots as one row of clickable thumbnails,
  // instead of a separate tab row above and a thumbnail row below.
  const filmstripItems = useMemo(() => {
    const items = [];

    mediaChoices.forEach((m) => {
      if (m.type === "youtube") {
        items.push({
          key: m.key,
          kind: "youtube",
          label: "Trailer",
          thumb: `https://img.youtube.com/vi/${m.id}/mqdefault.jpg`,
        });
      } else if (m.type === "mp4") {
        items.push({
          key: m.key,
          kind: "mp4",
          label: "Trailer",
          thumb: m.poster || null,
        });
      }
    });

    screenshots.forEach((src, i) => {
      items.push({ key: `img-${i}`, kind: "image", src, thumb: src, index: i });
    });

    return items;
  }, [mediaChoices, screenshots]);

  // Prev/Next only cycle through projects in the SAME category whose detail page
  // is enabled, so opening a Game only flips through other Games, a Game Jam only
  // flips through other Game Jams, etc. — new categories added later (Websites,
  // Tools, Art, ...) get this automatically since it keys off `project.category`.
  const currentCategory = project?.category || "other";

  const categoryProjects = useMemo(
    () =>
      allProjects.filter(
        (p) => p.detailsPageEnabled && (p.category || "other") === currentCategory
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allProjects, currentCategory]
  );

  const prevProject = useMemo(() => {
    if (categoryProjects.length < 2) return null;
    const index = categoryProjects.findIndex((p) => p.id === id);
    if (index === -1) return null;
    return categoryProjects[(index - 1 + categoryProjects.length) % categoryProjects.length];
  }, [categoryProjects, id]);

  const nextProject = useMemo(() => {
    if (categoryProjects.length < 2) return null;
    const index = categoryProjects.findIndex((p) => p.id === id);
    if (index === -1) return null;
    return categoryProjects[(index + 1) % categoryProjects.length];
  }, [categoryProjects, id]);

  // Keep media stable when switching projects
  useEffect(() => {
    setActiveImage(initialHero);
    setActiveMedia(mediaChoices.length > 0 ? mediaChoices[0].key : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Website projects: go straight to the target site (same tab)
  useEffect(() => {
    if (!project) return;
    const isWebsite = (project.category || "") === "websites" || project.openExternally === true;
    if (!isWebsite) return;
    const url = getPrimaryUrl(project);
    if (url) window.location.href = url;
  }, [project]);

  // Safe early return
  if (!project) {
    return (
      <div className="project-page project-page--not-found">
        <div className="project-page-inner">
          <button className="back-btn" onClick={() => history.push("/")}>
            ← Back
          </button>
          <h1>Project Not Found</h1>
        </div>
      </div>
    );
  }

  // --------------------- STATUS COLORS & LABELS ----------------------
  // Prefer the centralized helper (supports `project.status` OR matching strings inside `techTags`).
  const derivedStatus = getProjectStatusMeta(project);

  // Backwards compatibility for older data that sets `project.status` to known keys.
  const statusColors = {
    published: "#4CAF50",
    released: "#4CAF50",
    "in-development": "#1E88E5",
    prototype: "#FB8C00",
    archived: "#9E9E9E",
    canceled: "#E53935",
    "early-access": "#8E24AA",
  };

  const statusLabels = {
    published: "Published",
    released: "Released",
    "in-development": "In Development",
    prototype: "Prototype",
    archived: "Archived",
    canceled: "Canceled",
    "early-access": "Early Access",
  };

  const legacyKeyRaw = String(project.status || "").toLowerCase();
  const legacyKey = legacyKeyRaw.replace(/\s+/g, "-");
  const legacyColor = statusColors[legacyKey] || statusColors[legacyKeyRaw];
  const legacyLabel = statusLabels[legacyKey] || statusLabels[legacyKeyRaw];

  const statusColor = derivedStatus?.color || legacyColor || "#39ff14";
  const statusLabel = derivedStatus?.label || legacyLabel || "Unknown";
  const showStatusBadge = Boolean(derivedStatus || project.status);

  const visibleTags = stripStatusTags(project.techTags);

  // --------------------- THEME ----------------------
  const theme = project.projectTheme || {};

  // Default per-category sections so pages are useful even if you don't define `sections` on each project.
  const category = project.category || "other";
  const defaultSectionsByCategory = {
    games: {
      description: true,
      gallery: true,
      howToPlay: true,
      controls: true,
      features: true,
      technical: true,
      credits: true,
      devLogo: false,
      versionHistory: false,
      myContributions: true,
    },
    gamejams: {
      description: true,
      gallery: true,
      howToPlay: false,
      controls: true,
      features: true,
      technical: false,
      credits: true,
      devLogo: false,
      versionHistory: false,
      myContributions: true,
    },
    tools: {
      description: true,
      gallery: true,
      howToPlay: false,
      controls: false,
      features: true,
      technical: true,
      credits: true,
      devLogo: false,
      versionHistory: true,
      myContributions: true,
      toolDocs: true,
    },
    art: {
      description: true,
      gallery: true,
      howToPlay: false,
      controls: false,
      features: false,
      technical: false,
      credits: false,
      devLogo: false,
      versionHistory: false,
      myContributions: false,
      artDetails: true,
    },
    websites: {
      description: false,
      gallery: false,
    },
    other: {
      description: true,
      gallery: true,
      controls: true,
      features: true,
      technical: false,
      credits: true,
      myContributions: true,
    },
  };

  const sections = {
    ...(defaultSectionsByCategory[category] || defaultSectionsByCategory.other),
    ...(project.sections || {}),
  };


  return (
    <div
      className="project-page"
      style={{
        "--bg-color": theme.backgroundColor || siteTheme.bg || "#050507",
        "--bg-image": theme.backgroundImage
          ? `url(${theme.backgroundImage})`
          : "none",
        // Matches the real live accent (buttons / active tabs in Portfolio.scss &
        // StartupProjects.scss) — portfolio.js's theme.accent/accent2 (purple/cyan)
        // is unused config, applyTheme() is never called, so it isn't the site's
        // actual brand color despite being the "official" theme object.
        "--accent-color": theme.accentColor || "#00b74f",
        "--accent-color-2": theme.accentColor2 || "#00e064",
        "--card-color": theme.cardColor || "rgba(255,255,255,0.045)",
        "--card-border": theme.borderColor || "rgba(0,183,79,0.28)",
        "--text-color": theme.textColor || siteTheme.text || "#fff",
        "--button-color": theme.buttonColor || "#00b74f",
      }}
    >
      <div className="project-page-inner">
        {/* TOP BAR */}
        <div className="project-page-topbar">
          <button
            className="back-btn"
            onClick={() =>
              history.push({
                pathname: "/",
                state: { skipSplash: true, scrollTo: "portfolio" },
              })
            }
          >
            ← Back
          </button>

          {showStatusBadge && (
            <div
              className="project-status-badge"
              style={{ backgroundColor: statusColor }}
            >
              {statusLabel}
            </div>
          )}
        </div>

        <div className="project-layout">
          {/* ========================= SIDEBAR ========================= */}
          <aside className="project-sidebar">
            {/* Logo & Name */}
            <div className="sidebar-card sidebar-card--icon">
              {project.image && (
                <div className="sidebar-icon-wrapper">
                  <img
                    src={project.image}
                    alt={project.projectName}
                    className="sidebar-icon"
                  />
                </div>
              )}
              <h2 className="sidebar-title">{project.projectName}</h2>

              {project.projectDesc && (
                <p className="sidebar-tagline">{project.projectDesc}</p>
              )}

              {project.footerLink?.length > 0 && (
                <div className="sidebar-cta">
                  {project.footerLink.map((link, i) => (
                    <a
                      key={i}
                      className="sidebar-cta-btn"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.name} ↗
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Tags */}
            {visibleTags?.length > 0 && (
              <div className="sidebar-card">
                <h3 className="sidebar-heading">Tags</h3>
                <div className="sidebar-tags">
                  {visibleTags.map((tag, i) => (
                    <span key={i} className="sidebar-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Details */}
            {details.length > 0 && (
              <div className="sidebar-card">
                <h3 className="sidebar-heading">Details</h3>
                <div className="sidebar-details">
                  {details.map((row, i) => (
                    <div key={i} className="sidebar-detail-row">
                      <span className="sidebar-detail-label">{row.label}</span>
                      <span className="sidebar-detail-value">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Credits (Support images + text) */}
            {sections.credits && project.credits?.length > 0 && (
              <div className="sidebar-card">
                <h3 className="sidebar-heading">Credits</h3>

                {project.credits.map((entry, i) => (
                  <div key={i} className="credit-entry">
                    {typeof entry === "string" ? (
                      <span>{entry}</span>
                    ) : (
                      <>
                        {entry.avatar && (
                          <img
                            src={entry.avatar}
                            alt={entry.name}
                            className="credit-avatar"
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                        <div className="credit-text">
                          <span className="credit-name">{entry.name}</span>
                          <span className="credit-role">{entry.role}</span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Version History */}
            {sections.versionHistory && project.versionHistory?.length > 0 && (
              <div className="sidebar-card">
                <h3 className="sidebar-heading">Version History</h3>
                <ul className="sidebar-list">
                  {project.versionHistory.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          {/* ========================= MAIN CONTENT ========================= */}
          <main className="project-main">
            {/* Description */}
            {sections.description && (
              <section className="project-section project-section--hero">
                <p className="project-subtitle">
                  {project.longDescription || project.projectDesc}
                </p>
              </section>
            )}

            {/* GALLERY (only if there's actually media — no empty placeholder box) */}
            {sections.gallery &&
              (mediaChoices.length > 0 || Boolean(initialHero) || screenshots.length > 0) && (
              <section className="project-section">
                <div className="project-gallery-card">
                  {/* Active media */}
                  <div className="project-media-stage">
                    {activeMediaObj?.type === "mp4" && (
                      <video
                        className="project-video"
                        controls
                        playsInline
                        src={activeMediaObj.src}
                        poster={activeMediaObj.poster || undefined}
                      />
                    )}

                    {activeMediaObj?.type === "youtube" && (
                      <div className="project-video-wrapper">
                        <iframe
                          src={`https://www.youtube.com/embed/${activeMediaObj.id}`}
                          title="Project Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    )}

                    {/* Main Hero Image */}
                    {activeMediaObj == null && activeImage && (
                      <img
                        src={activeImage}
                        className="project-main-image"
                        alt="Main"
                      />
                    )}
                  </div>

                  {/* Filmstrip: trailer(s) + screenshots as one row (only when there's an actual choice) */}
                  {filmstripItems.length > 1 && (
                    <div className="project-filmstrip">
                      {filmstripItems.map((item) => {
                        const isActive =
                          item.kind === "image"
                            ? activeMedia === null && activeImage === item.src
                            : activeMedia === item.key;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={
                              isActive
                                ? "filmstrip-item filmstrip-item--active"
                                : "filmstrip-item"
                            }
                            aria-label={
                              item.kind === "image"
                                ? `View screenshot ${item.index + 1}`
                                : `Play ${item.label}`
                            }
                            onClick={() => {
                              if (item.kind === "image") {
                                setActiveMedia(null);
                                setActiveImage(item.src);
                              } else {
                                setActiveMedia(item.key);
                              }
                            }}
                          >
                            {item.thumb ? (
                              <img src={item.thumb} alt="" loading="lazy" decoding="async" />
                            ) : (
                              <span className="filmstrip-fallback">{item.label}</span>
                            )}
                            {item.kind !== "image" && (
                              <span className="filmstrip-play">▶</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* MY CONTRIBUTIONS */}
            {sections.myContributions &&
              (project.myContributions?.length > 0 || project.contributions?.length > 0) && (
                <section className="project-section">
                  <h2 className="section-heading">What I Did</h2>
                  <ul className="section-list">
                    {(project.myContributions || project.contributions).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </section>
              )}

            {/* TOOL DOCS */}
            {sections.toolDocs && (
              <>
                {project.useCases?.length > 0 && (
                  <section className="project-section">
                    <h2 className="section-heading">Use Cases</h2>
                    <ul className="section-list">
                      {project.useCases.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {project.installation?.length > 0 && (
                  <section className="project-section">
                    <h2 className="section-heading">Installation</h2>
                    <ol className="section-list">
                      {project.installation.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ol>
                  </section>
                )}

                {project.quickStart?.length > 0 && (
                  <section className="project-section">
                    <h2 className="section-heading">Quick Start</h2>
                    <ol className="section-list">
                      {project.quickStart.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ol>
                  </section>
                )}

                {project.api?.length > 0 && (
                  <section className="project-section">
                    <h2 className="section-heading">API</h2>
                    <ul className="section-list">
                      {project.api.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}

            {/* ART DETAILS */}
            {sections.artDetails && (
              <>
                {project.deliverables?.length > 0 && (
                  <section className="project-section">
                    <h2 className="section-heading">Deliverables</h2>
                    <ul className="section-list">
                      {project.deliverables.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </section>
                )}

                {project.process?.length > 0 && (
                  <section className="project-section">
                    <h2 className="section-heading">Process</h2>
                    <ul className="section-list">
                      {project.process.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}

            {/* HOW TO PLAY */}
            {sections.howToPlay && project.howToPlay?.length > 0 && (
              <section className="project-section">
                <h2 className="section-heading">How to Play</h2>
                <ul className="section-list">
                  {project.howToPlay.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* CONTROLS + FEATURES */}
            {((sections.controls && project.controls?.length > 0) ||
              (sections.features && project.features?.length > 0)) && (
              <section className="project-section two-column">
                {/* Controls */}
                {sections.controls && project.controls?.length > 0 && (
                  <div className="column">
                    <h2 className="section-heading">Controls</h2>
                    <ul className="section-list">
                      {project.controls.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Features */}
                {sections.features && project.features?.length > 0 && (
                  <div className="column">
                    <h2 className="section-heading">Features</h2>
                    <ul className="section-list">
                      {project.features.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {/* TECHNICAL DETAILS */}
            {sections.technical &&
              project.technicalDetails?.length > 0 && (
                <section className="project-section">
                  <h2 className="section-heading">Technical Details</h2>
                  <ul className="section-list">
                    {project.technicalDetails.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </section>
              )}

            {/* DEVELOPER LOGO */}
            {sections.devLogo && project.devLogo && (
              <section className="project-section">
                <h2 className="section-heading">Developer</h2>
                <img src={project.devLogo} alt="Dev Logo" className="dev-logo" />
              </section>
            )}

            {/* PREV / NEXT PROJECT */}
            {(prevProject || nextProject) && (
              <section className="project-section project-nav-section">
                {prevProject && (
                  <button
                    type="button"
                    className="project-nav-link project-nav-link--prev"
                    onClick={() => history.push(`/project/${prevProject.id}`)}
                  >
                    <span className="project-nav-arrow">←</span>
                    <span className="project-nav-text">
                      <span className="project-nav-label">Previous</span>
                      <span className="project-nav-name">{prevProject.projectName}</span>
                    </span>
                  </button>
                )}

                {nextProject && (
                  <button
                    type="button"
                    className="project-nav-link project-nav-link--next"
                    onClick={() => history.push(`/project/${nextProject.id}`)}
                  >
                    <span className="project-nav-text">
                      <span className="project-nav-label">Next</span>
                      <span className="project-nav-name">{nextProject.projectName}</span>
                    </span>
                    <span className="project-nav-arrow">→</span>
                  </button>
                )}
              </section>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
